/**
 * Utility functions for handling dashboard slugs in the format "id-name"
 * Provides backward compatibility and handles name changes gracefully
 */

/**
 * Creates a URL-safe slug from a string by:
 * - Converting to lowercase
 * - Replacing spaces and special characters with hyphens
 * - Removing consecutive hyphens
 * - Trimming hyphens from start/end
 */
export const createSlugFromName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters except spaces and hyphens
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple consecutive hyphens with single hyphen
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Creates a dashboard slug in the format "id-name"
 */
export const createDashboardSlug = (id: string, name: string): string => {
  const nameSlug = createSlugFromName(name);
  return `${id}-${nameSlug}`;
};

/**
 * Parses a dashboard slug to extract ID and name
 * Supports multiple formats:
 * - "123-dashboard-name" -> { id: "123", nameSlug: "dashboard-name" }
 * - "e593b5ef-6765-4b5e-894a-5b27fadcb6a5-dashboard-name" -> { id: "e593b5ef-6765-4b5e-894a-5b27fadcb6a5", nameSlug: "dashboard-name" }
 * - "123" -> { id: "123", nameSlug: null } (backward compatibility)
 * - "e593b5ef-6765-4b5e-894a-5b27fadcb6a5" -> { id: "e593b5ef-6765-4b5e-894a-5b27fadcb6a5", nameSlug: null } (backward compatibility)
 */
export const parseDashboardSlug = (
  slug: string,
): { id: string; nameSlug: string | null } => {
  // Handle pure numeric ID format (backward compatibility)
  if (/^\d+$/.test(slug)) {
    return { id: slug, nameSlug: null };
  }

  // Handle pure UUID format (backward compatibility)
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug)
  ) {
    return { id: slug, nameSlug: null };
  }

  // IMPORTANT: Check UUID id-name format BEFORE numeric id-name format
  // This prevents UUIDs starting with numbers from being incorrectly parsed
  // Example: "46229089-1234-5678-9abc-123456789012-dashboard-name" should be parsed as:
  // - id: "46229089-1234-5678-9abc-123456789012"
  // - nameSlug: "dashboard-name"
  // NOT as id: "46229089" and nameSlug: "1234-5678-9abc-123456789012-dashboard-name"

  // Handle UUID id-name format (must come before numeric pattern)
  const uuidMatch = slug.match(
    /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-(.+)$/i,
  );
  if (uuidMatch) {
    return { id: uuidMatch[1], nameSlug: uuidMatch[2] };
  }

  // Handle numeric id-name format (comes after UUID pattern to avoid conflicts)
  const numericMatch = slug.match(/^(\d+)-(.+)$/);
  if (numericMatch) {
    return { id: numericMatch[1], nameSlug: numericMatch[2] };
  }

  // Fallback: treat entire slug as ID (shouldn't happen but provides safety)
  return { id: slug, nameSlug: null };
};

/**
 * Checks if the current slug matches the expected format for a dashboard
 * Returns true if slug is correct, false if it needs to be updated
 */
export const isSlugCorrect = (
  currentSlug: string,
  dashboardId: string,
  dashboardName: string,
): boolean => {
  const { id, nameSlug } = parseDashboardSlug(currentSlug);
  const expectedNameSlug = createSlugFromName(dashboardName);

  return id === dashboardId && nameSlug === expectedNameSlug;
};

/**
 * Generates the correct URL path for a dashboard
 */
export const getDashboardPath = (id: string, name: string): string => {
  const slug = createDashboardSlug(id, name);
  return `/dashboard/${slug}`;
};

/**
 * Generates a full URL to a specific dashboard tab.
 * Format: /dashboard/{id}-{name-slug}?tab={tabName}
 */
export const getDashboardTabUrl = (
  dashboardId: string,
  dashboardName: string,
  tabName: string,
): string => {
  const slug = createDashboardSlug(dashboardId, dashboardName);
  return `/dashboard/${slug}?tab=${encodeURIComponent(tabName)}`;
};

/**
 * Checks if a slug needs to be redirected and returns the correct path
 * Returns null if no redirect is needed
 */
export const getRedirectPath = (
  currentSlug: string,
  dashboardId: string,
  dashboardName: string,
): string | null => {
  if (isSlugCorrect(currentSlug, dashboardId, dashboardName)) {
    return null;
  }

  return getDashboardPath(dashboardId, dashboardName);
};
